import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Download, Play, Clock, ArrowLeft, Share2, Copy, CheckCircle2, Camera } from "lucide-react";

// Same filename contract as VideosPage.jsx — see frontend/public/videos/README.md
const VIDEOS = [
  { id: "01", file: "01-regimen.mp4",             title: "Building a chemotherapy regimen", duration: "00:37", size: "2.2 MB", blurb: "Compose any regimen from the 187-drug catalog. Dedup engine catches duplicates in real time." },
  { id: "02", file: "02-treatment-calendar.mp4",  title: "Treatment calendar in three clicks", duration: "00:49", size: "2.6 MB", blurb: "Pick a regimen, set a date, watch every cycle drop onto the patient's calendar." },
  { id: "03", file: "03-reschedule-request.mp4",  title: "Reschedule request: tap to provider", duration: "00:55", size: "2.1 MB", blurb: "Patient taps reschedule. Provider approves. SMS + email + in-app fire in parallel." },
  { id: "04", file: "04-approvals-queue.mp4",     title: "Approval queue: one place, every request", duration: "00:30", size: "1.3 MB", blurb: "Reschedules, drug changes, regimen edits — all unified, one-click resolution, audit-logged." },
];

function useAvailability(videos) {
  const [available, setAvailable] = useState({});
  useEffect(() => {
    let cancelled = false;
    videos.forEach((v) => {
      fetch(`/videos/${v.file}`, { method: "HEAD" })
        .then((r) => {
          const ct = r.headers.get("content-type") || "";
          const ok = r.ok && ct.startsWith("video/");
          if (!cancelled) setAvailable((p) => ({ ...p, [v.id]: ok }));
        })
        .catch(() => { if (!cancelled) setAvailable((p) => ({ ...p, [v.id]: false })); });
    });
    return () => { cancelled = true; };
  }, [videos]);
  return available;
}

export default function VideoGalleryPage() {
  const available = useAvailability(VIDEOS);
  const [copied, setCopied] = useState("");

  const copyLink = (url) => {
    navigator.clipboard.writeText(window.location.origin + url);
    setCopied(url);
    setTimeout(() => setCopied(""), 2000);
  };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-16 pb-10">
        <Link to="/videos" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-400 mb-6" data-testid="gallery-back-link">
          <ArrowLeft className="w-4 h-4" />Back to videos page
        </Link>
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Video gallery &middot; download MP4s</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">All four MP4s. One page. Built for sharing.</h1>
        <p className="text-slate-400 mt-4 max-w-2xl">Drop these on LinkedIn, paste into WhatsApp, attach to investor emails. Each video is hosted on this server &mdash; no third-party embeds, no tracking pixels.</p>
      </section>

      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-20 space-y-4" data-testid="gallery-list">
        {VIDEOS.map((v, i) => {
          const ready = available[v.id] === true;
          const url = `/videos/${v.file}`;
          return (
            <div key={v.id} className="glass-card overflow-hidden fade-up" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`gallery-row-${i}`}>
              <div className="grid md:grid-cols-[200px_1fr_auto] gap-5 items-center p-5">
                {/* Thumbnail */}
                <div className="relative aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-emerald-500/20 to-[#0B1220] flex items-center justify-center">
                  <div className="absolute inset-0 marketing-3d-ring opacity-40">
                    <div className="ring r1" /><div className="ring r2" /><div className="ring r3" /><div className="ring r4" />
                  </div>
                  {ready ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="relative w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center shadow-2xl shadow-emerald-500/50 hover:scale-110 transition-transform">
                      <Play className="w-5 h-5 text-[#0B1220] ml-0.5" fill="currentColor" />
                    </a>
                  ) : (
                    <div className="relative w-12 h-12 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
                      <Camera className="w-5 h-5 text-amber-300" />
                    </div>
                  )}
                  <span className="absolute bottom-1.5 right-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-slate-300 font-mono">
                    <Clock className="w-2.5 h-2.5" />{v.duration}
                  </span>
                </div>

                {/* Meta */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-mono text-slate-500">{v.id}</span>
                    {!ready && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 rounded-full">Recording in production</span>}
                  </div>
                  <h3 className="font-bold text-lg mb-1 truncate">{v.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{v.blurb}</p>
                  <div className="text-xs text-slate-500 mt-2 font-mono">{v.file} &middot; {v.size}</div>
                </div>

                {/* Actions */}
                <div className="flex md:flex-col items-stretch gap-2 md:w-44">
                  <a
                    href={url}
                    download
                    aria-disabled={!ready}
                    onClick={(e) => { if (!ready) e.preventDefault(); }}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold transition-colors ${ready ? "bg-emerald-500 hover:bg-emerald-400 text-[#0B1220]" : "bg-white/[0.04] text-slate-600 cursor-not-allowed"}`}
                    data-testid={`gallery-download-${i}`}
                  >
                    <Download className="w-4 h-4" />Download MP4
                  </a>
                  <button
                    onClick={() => copyLink(url)}
                    disabled={!ready}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold border transition-colors ${ready ? "border-white/10 hover:border-emerald-500/40 text-slate-200 hover:text-white" : "border-white/[0.04] text-slate-600 cursor-not-allowed"}`}
                    data-testid={`gallery-share-${i}`}
                  >
                    {copied === url ? <><CheckCircle2 className="w-4 h-4 text-emerald-400" />Copied</> : <><Share2 className="w-4 h-4" />Copy link</>}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-20">
        <div className="glass-card p-7 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-1">Want a tailored walk-through?</div>
            <h3 className="font-bold text-lg">Skip the videos &mdash; see your own workflow in 30 minutes.</h3>
          </div>
          <Link to="/demo" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold transition-colors whitespace-nowrap">
            Request a live demo
          </Link>
        </div>
      </section>
    </div>
  );
}
